import { type DropzoneProps, Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { styled, useTheme } from '@mui/material';
import { useRef, forwardRef, type ForwardedRef } from 'react';

import { UploadFileOutlinedIcon } from '../../icons';
import { Button, Flex, Text } from '../../index';

const Wrapper = styled(Flex)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3.5),
  gap: theme.spacing(2),
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const FileItemWrapper = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  width: '100%',
}));

export interface BaseFileInputProps extends Omit<DropzoneProps, 'children'> {
  buttonText?: string;
  value?: File | File[];
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  startIcon?: React.ReactNode;
  multiple?: boolean;
}

function FileInput(props: Readonly<BaseFileInputProps>, ref: ForwardedRef<HTMLDivElement>) {
  const {
    buttonText,
    error: isError,
    disabled: isDisabled,
    placeholder,
    startIcon,
    multiple: isMultiple = false,
    ...restProps
  } = props;

  const theme = useTheme();
  const openRef = useRef<() => void>(null);

  return (
    <Dropzone
      openRef={openRef}
      disabled={isDisabled}
      sx={{
        width: '100%',
        borderRadius: '4px',
        padding: 0,
        // Note: Consider moving this styling to styled instead
        ...(isDisabled && {
          cursor: 'default',
          backgroundColor: theme.palette.grey[100],
          ':hover': { backgroundColor: theme.palette.grey[100] },
        }),
        borderColor: isError ? theme.palette.error.main : theme.palette.grey[400],
        color: isError ? theme.palette.error.main : theme.palette.text.secondary,
        '&[data-accept]': { color: theme.palette.info.main },
        '&[data-reject]': { color: theme.palette.error.main },
      }}
      multiple={isMultiple}
      {...restProps}
    >
      <Wrapper sx={{ py: 2.5 }} ref={ref}>
        <FileItemWrapper>
          {startIcon ?? (
            <UploadFileOutlinedIcon
              sx={{ ml: 0.5, color: 'text.disabled', width: 24, height: 24 }}
            />
          )}
          <Text sx={{ color: 'text.disabled', width: 1 }}>{placeholder}</Text>
          <Button
            color='secondary'
            variant='outlined'
            onClick={() => openRef.current?.()}
            disabled={isDisabled}
            sx={{ borderRadius: '4px', minWidth: 'fit-content' }}
            size='small'
          >
            {buttonText ?? 'เลือกไฟล์'}
          </Button>
        </FileItemWrapper>
      </Wrapper>
    </Dropzone>
  );
}

const BaseFileInput = forwardRef(FileInput);

export default BaseFileInput;

export { MIME_TYPES };
